/**
 * API Response Models
 * These types match the CommonLib.Models from the backend
 */

/**
 * Normalized API response message
 * Corresponds to ResponseMessage<T> in CommonLib
 */
export interface ResponseMessage<T = unknown> {
  payload?: T | null;
  status: number;
  detail?: string | null;
  path?: string | null;
  message?: string | null;
}

/**
 * Paginated result set
 * Corresponds to PagedResult<T> in CommonLib
 */
export interface PagedResult<T> {
  payload: T[];
  items: T[]; // Alias for payload (backward compatibility)
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

/**
 * Backend API paged response format
 * This is what the .NET API actually returns
 */
export interface BackendPagedResponse<T> {
  payload: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

/**
 * Transforms backend paged response to frontend PagedResult
 * Adds items alias for backward compatibility
 */
export function toPagedResult<T>(
  response: BackendPagedResponse<T>,
): PagedResult<T> {
  return {
    ...response,
    items: response.payload, // Create alias
  };
}

/**
 * Request for paginated data
 * Corresponds to PagedRequest in CommonLib
 */
export interface PagedRequest {
  pageNumber: number;
  pageSize: number;
}

/**
 * Base list params for search/filter operations
 * Common fields used across list/search endpoints
 * Uses PascalCase to match .NET API convention
 */
export interface BaseListParams {
  Page?: number;
  PageSize?: number;
  Query?: string;
  Sort?: string;
  Direction?: 'ASC' | 'DESC';
}

/**
 * API Error Response
 */
export interface ApiError {
  status: number;
  message: string;
  detail?: string;
  path?: string;
}

/**
 * Generic API success response
 */
export type ApiResponse<T> = ResponseMessage<T>;

/**
 * Paginated API response
 */
export type PagedResponse<T> = ResponseMessage<PagedResult<T>>;
